import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import {
  LayoutDashboard, Users as UsersIcon, Package, ShoppingCart,
  FileText, Receipt, BarChart3, UserCog, LogOut, Printer, Repeat,
  CheckSquare, CreditCard, Upload, Phone, TrendingUp, Menu, X,
  Calculator, AlertTriangle, Layers, MapPin, Database, Sparkles, Truck, PackageCheck, Trophy, Wallet
} from "lucide-react";

const navLinks = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/tasks", label: "To-Do & Plans", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/followups", label: "Follow-ups", icon: Phone, testid: "nav-followups" },
  { to: "/customers", label: "Customers", icon: UsersIcon, testid: "nav-customers" },
  { to: "/churn", label: "Churn Alerts", icon: AlertTriangle, testid: "nav-churn" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/variants", label: "Variants", icon: Layers, testid: "nav-variants" },
  { to: "/pricing", label: "Pricing Calculator", icon: Calculator, testid: "nav-pricing" },
  { to: "/visits", label: "Visit Planner", icon: MapPin, testid: "nav-visits" },
  { to: "/orders", label: "Orders", icon: ShoppingCart, testid: "nav-orders" },
  { to: "/pending-orders", label: "Pending Deliveries", icon: PackageCheck, testid: "nav-pending-orders" },
  { to: "/delivery-route", label: "Delivery Route AI", icon: Truck, testid: "nav-delivery-route" },
  { to: "/reorders", label: "Reorder Radar", icon: Repeat, testid: "nav-reorders" },
  { to: "/quotations", label: "Quotations", icon: FileText, testid: "nav-quotations" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/payment-collection", label: "Collections", icon: CreditCard, testid: "nav-collections" },
  { to: "/payment-dashboard", label: "Payment Dashboard", icon: Wallet, testid: "nav-payment-dashboard" },
  { to: "/incentives", label: "Incentives", icon: Trophy, testid: "nav-incentives" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]" data-testid="app-layout">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E5E7EB] px-3 py-2.5 flex items-center justify-between" data-testid="mobile-topbar">
        <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" className="p-2 rounded hover:bg-[#F1F5F9]">
          {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
        </button>
        <div className="flex items-center gap-2">
          <div className="brand-ribbon w-7 h-7 rounded flex items-center justify-center">
            <Printer className="text-white" size={14} strokeWidth={1.75} />
          </div>
          <span className="font-display font-bold text-sm">SINGH OFFSET</span>
        </div>
        <div className="w-9" />
      </div>

      {/* Sidebar */}
      <aside className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-30 w-60 bg-white border-r border-[#E5E7EB] flex flex-col transition-transform duration-200 ease-out`} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-[#E5E7EB] hidden lg:block">
          <div className="flex items-center gap-2.5">
            <div className="brand-ribbon w-9 h-9 rounded flex items-center justify-center">
              <Printer className="text-white" size={18} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight text-[15px] leading-tight">SINGH OFFSET</div>
              <div className="text-[9px] uppercase tracking-[0.16em] text-[#C1121F] font-semibold">Press CRM</div>
            </div>
          </div>
        </div>
        <div className="lg:hidden h-12 border-b border-[#E5E7EB]" />

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <l.icon size={17} strokeWidth={1.75} />
              <span>{l.label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <NavLink
                to="/raw-costs"
                data-testid="nav-raw-costs"
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <Database size={17} strokeWidth={1.75} />
                <span>Raw Costs</span>
              </NavLink>
              <NavLink
                to="/ai-instructions"
                data-testid="nav-ai-instructions"
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <Sparkles size={17} strokeWidth={1.75} />
                <span>AI Pricing Rules</span>
              </NavLink>
              <NavLink
                to="/users"
                data-testid="nav-users"
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <UserCog size={17} strokeWidth={1.75} />
                <span>Team &amp; Targets</span>
              </NavLink>
              <NavLink
                to="/profit"
                data-testid="nav-profit"
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <TrendingUp size={17} strokeWidth={1.75} />
                <span>Profit Report</span>
              </NavLink>
              <NavLink
                to="/import"
                data-testid="nav-import"
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              >
                <Upload size={17} strokeWidth={1.75} />
                <span>Import Data</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-[#E5E7EB] p-3">
          <div className="flex items-center gap-2.5 px-2 py-2 mb-1" data-testid="user-info">
            <div className="w-8 h-8 rounded-full bg-[#C1121F] text-white flex items-center justify-center font-display font-bold text-sm">
              {(user?.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="nav-item w-full text-left"
          >
            <LogOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} className="lg:hidden fixed inset-0 z-20 bg-black/40 backdrop-blur-sm" />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pt-14 lg:pt-0" data-testid="main-content">
        <div className="page-enter">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
